import { useState, useEffect, useRef, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { toast } from "sonner"
import { supabase, getActivityEndAt, getActivityPostSessionAnswer, isSuiviSeul } from "@/lib/supabase"
import {
  CalendarDays,
  Bell,
  Check,
  ChevronRight,
  Plus,
} from "lucide-react"
import { getPlanForGoal } from "@/data/nutritionPlans"
import { MOCK_SESSION_S1 } from "@/data/mockSession"
import { useNotifications } from "@/hooks/useNotifications"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useOnboarding, type TipKey } from "@/hooks/useOnboarding"
import { useAuth } from "@/contexts/AuthContext"
import { usePushSubscription } from "@/hooks/usePushSubscription"
import { useNextProgramSession } from "@/hooks/useNextProgramSession"
import { useSubscription } from "@/hooks/useSubscription"
import { useMonitoring } from "@/hooks/useMonitoring"
import { useCalendarEvents } from "@/hooks/useCalendarEvents"
import { toLocalISODate } from "@/lib/utils"
import { OnboardingTip } from "@/components/OnboardingTip"
import { BottomNav } from "@/components/BottomNav"
import { ClockGauge } from "@/components/monitoring/ClockGauge"
import { AddFreeSessionSheet } from "@/components/calendar/AddEventFlow"

interface DashboardHomeProps {
  email: string
  firstName?: string
  offerId?: string
  goalId?: string
}

const MOTIVATION_PHRASES = [
  "Prêt à dépasser tes limites ?",
  "Ta progression t'attend !",
  "Chaque répétition compte.",
  "Pas d'excuses, que des résultats.",
  "Le meilleur investissement, c'est toi.",
  "La discipline bat la motivation.",
  "Repousse tes limites aujourd'hui.",
  "Ton futur toi te remerciera.",
  "La régularité fait la différence.",
  "C'est maintenant que ça se joue.",
  "Un pas de plus vers ton objectif.",
  "La douleur est temporaire, la fierté est permanente.",
  "Construis la meilleure version de toi.",
  "Ton corps peut le faire, convaincs ton esprit.",
]

function getDailyPhrase(): string {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000)
  return MOTIVATION_PHRASES[dayOfYear % MOTIVATION_PHRASES.length]
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return "Bonjour"
  if (hour < 18) return "Bon après-midi"
  return "Bonsoir"
}

function getFormattedDate(): string {
  return new Date().toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "long", year: "numeric" })
}

function formatDayLong(eventDate: string): string {
  const [y, m, d] = eventDate.split("-").map(Number)
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
}

function formatNextSessionDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number)
  const label = new Date(y, m - 1, d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

// Lundi de la semaine (locale) contenant `d`.
function getWeekStartMonday(d: Date): Date {
  const day = d.getDay() // 0=dimanche..6=samedi
  const diff = (day + 6) % 7 // jours depuis lundi
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff)
}

const WEEK_DAY_LETTERS = ["L", "M", "M", "J", "V", "S", "D"]

// Titre de section : tiret dégradé + libellé en majuscules espacées, avec un slot
// à droite — par défaut un lien "Détails" (navigue vers `onDetails` s'il est fourni,
// simple raccourci vers la même destination que la tuile en dessous, qui reste
// entièrement cliquable elle-même), ou tout autre contenu (ex. le compteur de
// "Cette semaine") pour rester cohérent visuellement.
function SectionTitle({ label, right, onDetails }: { label: string; right?: ReactNode; onDetails?: () => void }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="w-[16px] h-[2px] rounded-full shrink-0" style={{ background: "linear-gradient(90deg, #06b6d4, transparent)" }} />
        <span className="text-[10px] font-bold text-gray-300 uppercase" style={{ letterSpacing: 2 }}>{label}</span>
      </div>
      {right ?? (
        onDetails ? (
          <button
            onClick={onDetails}
            className="flex items-center gap-0.5 text-[10.5px] font-semibold text-cyan-400 cursor-pointer transition-opacity active:opacity-70"
          >
            Détails <ChevronRight className="w-3 h-3" />
          </button>
        ) : (
          <span className="flex items-center gap-0.5 text-[10.5px] font-semibold text-cyan-400">
            Détails <ChevronRight className="w-3 h-3" />
          </span>
        )
      )}
    </div>
  )
}

const pulseStyle = `
@keyframes btn-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(6,182,212,0.45); }
  50% { box-shadow: 0 0 0 10px rgba(6,182,212,0); }
}
.pulse-btn { animation: btn-pulse 2.2s ease-in-out infinite; }
`

type TipId = "welcome" | "post_hooper" | "push_j3"

// welcome.message est calculé dynamiquement à partir de la date de la première séance
const TIP_CONFIG: Record<TipId, { message: string; key: TipKey; actionLabel?: string; actionPath?: string }> = {
  welcome:     { message: "", key: "tip_welcome_seen", actionLabel: "Voir le calendrier", actionPath: "/dashboard/calendar" },
  post_hooper: { message: "Ton score Hooper est calculé. Va voir ton monitoring", key: "tip_post_hooper_seen", actionLabel: "Voir le monitoring", actionPath: "/dashboard/monitoring" },
  push_j3:     { message: "Active les notifications pour ne rien rater", key: "tip_push_j3_seen", actionLabel: "Activer", actionPath: "/dashboard/notifications" },
}

export const DashboardHome = ({ email: _email, firstName, offerId: _offerId, goalId }: DashboardHomeProps) => {
  const navigate = useNavigate()
  const [currentPhrase, setCurrentPhrase] = useState(getDailyPhrase())
  const { unreadCount } = useNotifications()
  const { track } = useAnalytics()
  const { state: onboardingState, loading: onboardingLoading, isJ3, hasHooper, markTipSeen } = useOnboarding()
  const { isSubscribed } = usePushSubscription()
  const { nextSessionLabel, nextSessionDate, nextSessionTime, loaded: nextPsnLoaded } = useNextProgramSession()
  const { isActive: isSubscriberActive, inTrial, trialEnded, loading: subLoading } = useSubscription()
  const { refreshWizardData, wizardData } = useAuth()
  // Suivi seul : aucune séance Zeyone proposée — la carte de séance propose d'en ajouter
  // une (séance libre, même parcours que le calendrier), la semaine compte les siennes.
  const suiviSeul = isSuiviSeul(wizardData)
  const [addSessionOpen, setAddSessionOpen] = useState(false)
  const { todayHooper, avg7Hooper, acwr, strain, loading: monitoringLoading } = useMonitoring()

  // Date locale (pas toISOString/UTC) pour rester synchronisé avec CalendarPage
  // (useCalendarEvents.getMonthBounds/isDateToday), dont "aujourd'hui" est calculé
  // à partir de getFullYear/getMonth/getDate en heure locale.
  const now = new Date()
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
  // Plus de garde « séance n° ≥ 2 » : la semaine d'essai ouvre tout (D2). Le
  // bouton ne bascule que quand l'essai est bel et bien terminé, et il mène au
  // bilan + aux offres, pas à la page de facturation.
  //
  // `!subLoading` est indispensable : sans lui, `isSubscriberActive` vaut false
  // le temps de la lecture du droit et l'accueil afficherait un cadenas à chaque
  // chargement, y compris pour un abonné.
  const needsSubscription = !subLoading && !isSubscriberActive
  // Bouton actif si : hook pas encore chargé (pas de flicker), ou aucune date connue,
  // ou séance planifiée aujourd'hui, ou séance en retard (date passée non complétée)
  const showStartButton = !nextPsnLoaded || !nextSessionDate || nextSessionDate <= todayIso
  const isLate = nextPsnLoaded && !!nextSessionDate && nextSessionDate < todayIso

  // Semaine courante (lundi → dimanche) pour la barre hebdomadaire de séances.
  const weekStart = getWeekStartMonday(now)
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart)
    d.setDate(weekStart.getDate() + i)
    return d
  })
  const { events: weekEvents, addEvent: addWeekEvent } = useCalendarEvents(toLocalISODate(weekDates[0]), toLocalISODate(weekDates[6]))
  // Suivi seul : ses séances libres (sauf « Je n'ai pas fait cette séance ») — faite une
  // fois l'heure de fin passée, prévue avant. Pas d'état « en retard » : rien n'est imposé.
  const isFreeSessionDone = (e: (typeof weekEvents)[number]) => {
    const endAt = getActivityEndAt(e)
    return !endAt || endAt.getTime() <= now.getTime()
  }
  const weekSessionEvents = suiviSeul
    ? weekEvents.filter((e) =>
        e.type === "seance-done" ||
        (e.type === "seance-libre" && getActivityPostSessionAnswer(e)?.status !== "skipped"))
    : weekEvents.filter((e) => e.type === "seance-planned" || e.type === "seance-done")
  const weekDays = weekDates.map((d, i) => {
    const iso = toLocalISODate(d)
    const isToday = iso === todayIso
    const dayEvents = weekSessionEvents.filter((e) => e.event_date === iso)
    let state: "done" | "late" | "today" | "planned" | "rest"
    if (suiviSeul) {
      if (dayEvents.some((e) => e.type === "seance-done" || isFreeSessionDone(e))) state = "done"
      else if (isToday) state = "today"
      else if (dayEvents.length > 0) state = "planned"
      else state = "rest"
    } else {
      const ev = dayEvents[0]
      if (ev?.type === "seance-done") state = "done"
      else if (ev?.type === "seance-planned" && iso < todayIso) state = "late"
      else if (isToday) state = "today"
      else if (ev?.type === "seance-planned") state = "planned"
      else state = "rest"
    }
    return { iso, dayNumber: d.getDate(), label: WEEK_DAY_LETTERS[i], state }
  })
  const weekDone = suiviSeul
    ? weekSessionEvents.filter((e) => e.type === "seance-done" || isFreeSessionDone(e)).length
    : weekSessionEvents.filter((e) => e.type === "seance-done").length
  const weekTotal = weekSessionEvents.length

  // Récupération (Hooper), ACWR et Strain — mêmes calculs que la page Monitoring.
  const hooperZone = todayHooper ?? { label: "—", color: "#94a3b8" }
  const hooperScore = todayHooper?.score ?? avg7Hooper ?? 0

  const activeTip: TipId | null = (() => {
    // subLoading : le texte du tip d'accueil dépend de l'essai — pas de changement de texte sous les yeux.
    if (onboardingLoading || !onboardingState || !nextPsnLoaded || subLoading) return null
    // L'accueil et le calendrier annoncent désormais LA MÊME séance : celle du
    // programme. Avant le lot 2, ce message parlait de la séance d'essai créée par
    // create-trial-session — un événement sans programme_session_number, donc sans
    // rapport avec la séance 1 proposée ici.
    if (!onboardingState.tip_welcome_seen && nextSessionDate) return "welcome"
    if (!onboardingState.tip_post_hooper_seen && hasHooper) return "post_hooper"
    if (!onboardingState.tip_push_j3_seen && isJ3 && !isSubscribed) return "push_j3"
    return null
  })()

  useEffect(() => {
    let cancelled = false
    async function checkLevelUp() {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || cancelled) return
      const { data: wizard } = await supabase
        .from("wizard_submissions")
        .select("level_id, level_up")
        .eq("user_id", user.id)
        .maybeSingle()
      if (!wizard?.level_up || cancelled) return
      const message = wizard.level_id === "advanced"
        ? "🏆 Tu passes au niveau Avancé — Bravo, il est temps de rejoindre une salle pour continuer ta progression !"
        : "🏆 Tu passes au niveau Intermédiaire — Bravo, continue sur cette lancée !"
      toast.success(message, { duration: 6000 })
      await supabase
        .from("wizard_submissions")
        .update({ level_up: false })
        .eq("user_id", user.id)
      // wizardData en mémoire (AuthContext) était encore sur l'ancien niveau — resynchronise
      // pour que SessionPage/computeChargeKg etc. voient le level_id à jour sans reconnexion.
      if (!cancelled) await refreshWizardData()
    }
    checkLevelUp()
    return () => { cancelled = true }
  }, [refreshWizardData])

  useEffect(() => {
    let cancelled = false
    async function checkPostSessionPending() {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || cancelled) return
      const { data: wizard } = await supabase
        .from("wizard_submissions")
        .select("post_session_pending")
        .eq("user_id", user.id)
        .maybeSingle()
      if (!wizard?.post_session_pending || cancelled) return
      toast.info(
        "Bravo ! Ta notification questionnaire post-séance arrive dans 30 min — pense à la remplir pour débloquer ta prochaine séance.",
        { duration: 14000 }
      )
      await supabase
        .from("wizard_submissions")
        .update({ post_session_pending: false })
        .eq("user_id", user.id)
    }
    checkPostSessionPending()
    return () => { cancelled = true }
  }, [])

  const hasTrackedView = useRef(false)
  useEffect(() => {
    if (!hasTrackedView.current) {
      track('dashboard_first_view', { goal_id: goalId })
      hasTrackedView.current = true
    }
  }, [track])

  useEffect(() => { setCurrentPhrase(getDailyPhrase()) }, [])

  const displayName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase() : "Athlète"

  // Calories dynamiques selon l'objectif
  const plan = getPlanForGoal(goalId)
  const caloriesPerDay = plan.dailyCalories
  const macroRows = [
    { label: "Protéines", color: "#a78bfa", value: plan.macros.protein, pct: plan.macrosPct.protein },
    { label: "Glucides", color: "#f59e0b", value: plan.macros.carbs, pct: plan.macrosPct.carbs },
    { label: "Lipides", color: "#14b8a6", value: plan.macros.fat, pct: plan.macrosPct.fat },
  ]

  // Nom de séance : "partie du corps — méthode" — vient directement du mock pour l'instant
  // (sera remplacé par la séance du jour réelle quand on branchera programs/program_sessions)
  const sessionName = MOCK_SESSION_S1.displayName ?? "Séance du jour"

  return (
    <div className="min-h-screen bg-[#060a13] pb-28 cursor-default relative">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: "radial-gradient(80% 50% at 70% 0%, rgba(6,182,212,0.10), transparent 60%), radial-gradient(60% 40% at 20% 100%, rgba(3,105,161,0.10), transparent 60%)" }} />
        <img src="/dashboard-bg.png" alt="" className="w-full h-full object-cover opacity-[0.07]" />
      </div>
      <style>{pulseStyle}</style>

      {/* HEADER */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-5 pt-5 pb-2 relative z-10">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-[22px] font-bold text-white tracking-tight">{getGreeting()}, {displayName} !</h1>
            <p className="text-[12px] text-gray-500 mt-1">{getFormattedDate()}</p>
          </div>
          <button
            onClick={() => navigate("/dashboard/notifications")}
            className="relative w-9 h-9 rounded-lg bg-gray-800/50 border border-gray-800/60 flex items-center justify-center hover:bg-gray-700/50 transition-colors cursor-pointer mt-1 backdrop-blur-md"
          >
            <Bell className="w-3.5 h-3.5 text-gray-500" />
            {unreadCount > 0 && (
              <div className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full flex items-center justify-center px-1"
                style={{ background: "#ef4444", boxShadow: "0 0 8px rgba(239,68,68,0.4)" }}>
                <span className="text-[9px] font-bold text-white leading-none">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              </div>
            )}
          </button>
        </div>
      </motion.div>

      {/* ONBOARDING TIP */}
      {activeTip !== null && (
        <OnboardingTip
          message={activeTip === "welcome" && nextSessionDate
            // Semaine d'essai : 4 séances planifiées d'un coup (lot 2) — annoncer « ta
            // première séance » laissait croire qu'il n'y en avait qu'une (lot 3).
            ? inTrial
              ? "Ta semaine d'essai de 4 séances est planifiée dans le calendrier. Clique pour la voir."
              : `Ta première séance est planifiée pour ${formatDayLong(nextSessionDate)}. Clique pour la voir.`
            : TIP_CONFIG[activeTip].message}
          onDismiss={() => markTipSeen(TIP_CONFIG[activeTip].key)}
          actionLabel={TIP_CONFIG[activeTip].actionLabel}
          onAction={TIP_CONFIG[activeTip].actionPath
            ? () => navigate(TIP_CONFIG[activeTip].actionPath!)
            : undefined}
        />
      )}

      {/* CETTE SEMAINE */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }} className="px-4 mt-3 relative z-10">
        <div className="rounded-2xl p-4" style={{ background: "rgba(17,24,39,0.7)", border: "1px solid rgba(31,41,55,0.6)", backdropFilter: "blur(12px)" }}>
          <SectionTitle
            label="Cette semaine"
            right={suiviSeul ? (
              weekDone > 0 && (
                <p className="text-[12px] font-semibold text-gray-400 tabular-nums">
                  <span className="text-white">{weekDone}</span> <span className="text-gray-500 font-normal">séance{weekDone > 1 ? "s" : ""}</span>
                </p>
              )
            ) : weekTotal > 0 && (
              <p className="text-[12px] font-semibold text-gray-400 tabular-nums">
                <span className="text-white">{weekDone}</span>/{weekTotal} <span className="text-gray-500 font-normal">séances</span>
              </p>
            )}
          />
          <div className="grid grid-cols-7 gap-1.5">
            {weekDays.map((d) => {
              const isToday = d.state === "today"
              const isDone = d.state === "done"
              const isLateDay = d.state === "late"
              const isPlanned = d.state === "planned"
              const bg = isToday
                ? "linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)"
                : isDone ? "rgba(34,197,94,0.12)"
                : isLateDay ? "rgba(249,115,22,0.10)"
                : isPlanned ? "rgba(255,255,255,0.04)"
                : "rgba(255,255,255,0.02)"
              const border = isToday
                ? "1px solid rgba(255,255,255,0.18)"
                : isDone ? "1px solid rgba(34,197,94,0.28)"
                : isLateDay ? "1px solid rgba(249,115,22,0.28)"
                : "1px solid rgba(31,41,55,0.6)"
              const labelColor = isToday ? "rgba(255,255,255,0.9)" : isDone ? "#22c55e" : isLateDay ? "#f97316" : isPlanned ? "#94a3b8" : "#64748b"
              const dateColor = isToday ? "#fff" : isDone || isLateDay || isPlanned ? "#f8fafc" : "#64748b"
              return (
                <div key={d.iso} className="rounded-xl py-2 flex flex-col items-center gap-1"
                  style={{ background: bg, border, boxShadow: isToday ? "0 6px 18px rgba(6,182,212,0.35)" : "none" }}>
                  <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: labelColor }}>{d.label}</span>
                  <span className="text-[13px] font-bold tabular-nums" style={{ color: dateColor }}>{d.dayNumber}</span>
                  <div className="h-2.5 flex items-center justify-center">
                    {isDone && <Check className="w-2.5 h-2.5" style={{ color: "#22c55e" }} strokeWidth={3.5} />}
                    {isLateDay && <span className="w-[5px] h-[5px] rounded-full" style={{ background: "#f97316" }} />}
                    {isPlanned && <span className="w-[4px] h-[4px] rounded-full" style={{ background: "#06b6d4", opacity: 0.6 }} />}
                    {isToday && <span className="w-[4px] h-[4px] rounded-full bg-white" />}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </motion.div>

      {/* SESSION CARD */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="px-4 mt-4 relative z-10">
        <div className="relative rounded-[22px] overflow-hidden" style={{ minHeight: 300, border: "1px solid rgba(31,41,55,0.6)" }}>
          <img src="/session-bg.jpg" alt="" className="absolute inset-0 w-full h-full object-cover scale-[1.08]" style={{ objectPosition: "center 40%" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(6,10,19,0.05) 0%, rgba(6,10,19,0.22) 28%, rgba(6,10,19,0.75) 58%, rgba(6,10,19,0.95) 82%, rgba(6,10,19,0.98) 100%)" }} />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 80% at 80% 20%, rgba(6,182,212,0.16) 0%, transparent 55%)" }} />
          <div className="relative z-10 px-5 flex flex-col justify-between" style={{ minHeight: 300 }}>
            <div className="pt-5"><h2 className="text-[21px] font-bold text-white leading-snug drop-shadow-lg">{currentPhrase}</h2></div>
            <div className="pb-5">
              {suiviSeul ? (
                needsSubscription ? (
                  <button
                    onClick={() => navigate(trialEnded ? "/fin-essai" : "/dashboard/billing")}
                    className="w-full py-4 rounded-xl text-[16px] font-bold flex items-center justify-center gap-2.5 cursor-pointer transition-transform active:scale-[0.96]"
                    style={{ background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.30)", color: "#06b6d4" }}>
                    <span style={{ fontSize: 16 }}>🔒</span>
                    {trialEnded ? "Voir mon bilan et les offres" : "S'abonner pour continuer"}
                  </button>
                ) : (
                  <>
                    <p className="text-[13px] text-gray-300 mb-4 leading-relaxed">
                      Une séance avec ton coach ? Ajoute-la pour suivre ta charge et ta récupération.
                    </p>
                    <button onClick={() => setAddSessionOpen(true)} disabled={subLoading}
                      className="w-full py-4 rounded-xl text-[16px] font-bold text-white flex items-center justify-center gap-2.5 cursor-pointer transition-transform active:scale-[0.96] disabled:opacity-60"
                      style={{ background: "linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)", boxShadow: "0 10px 30px rgba(6,182,212,0.35), inset 0 1px 0 rgba(255,255,255,0.2)" }}>
                      <Plus className="w-5 h-5" strokeWidth={2.5} />
                      Ajouter une séance
                    </button>
                  </>
                )
              ) : !nextPsnLoaded ? (
                <>
                  <div className="h-5 w-44 rounded-lg bg-white/10 animate-pulse mb-4" />
                  <div className="h-14 rounded-xl bg-white/10 animate-pulse" />
                </>
              ) : (
                <>
                  <p className="text-[16px] text-cyan-400 font-semibold mb-4" style={{ textShadow: "0 0 12px rgba(6,182,212,0.4)" }}>
                    {nextSessionLabel ?? sessionName}
                  </p>
                  {needsSubscription ? (
                    <button
                      onClick={() => navigate(trialEnded ? "/fin-essai" : "/dashboard/billing")}
                      className="w-full py-4 rounded-xl text-[16px] font-bold flex items-center justify-center gap-2.5 cursor-pointer transition-transform active:scale-[0.96]"
                      style={{ background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.30)", color: "#06b6d4" }}>
                      <span style={{ fontSize: 16 }}>🔒</span>
                      {trialEnded ? "Voir mon bilan et les offres" : "S'abonner pour continuer"}
                    </button>
                  ) : showStartButton ? (
                    <>
                      {isLate && (
                        <div className="rounded-xl px-4 py-2.5 flex items-center gap-2.5 mb-3"
                          style={{ background: "rgba(249,115,22,0.10)", border: "1px solid rgba(249,115,22,0.25)" }}>
                          <span style={{ color: "#f97316", fontSize: 14 }}>⚠</span>
                          <p className="text-[12px] font-semibold" style={{ color: "#f97316" }}>Séance en retard</p>
                        </div>
                      )}
                      <button onClick={() => navigate("/dashboard/session")}
                        className="pulse-btn w-full py-4 rounded-xl text-[16px] font-bold text-white flex items-center justify-center gap-2.5 cursor-pointer transition-transform active:scale-[0.96]"
                        style={{ background: "linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)", boxShadow: "0 10px 30px rgba(6,182,212,0.35), inset 0 1px 0 rgba(255,255,255,0.2)" }}>
                        <svg width="14" height="16" viewBox="0 0 14 16" fill="white"><path d="M0 0L14 8L0 16V0Z" /></svg>
                        Démarrer la séance
                      </button>
                    </>
                  ) : (
                    <div className="rounded-xl px-4 py-3.5 flex items-center gap-3"
                      style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}>
                      <CalendarDays className="w-4 h-4 text-gray-400 shrink-0" />
                      <div>
                        <p className="text-[11px] text-gray-500 mb-0.5">Prochaine séance</p>
                        <p className="text-[14px] font-semibold text-white">
                          {formatNextSessionDay(nextSessionDate!)}
                          {nextSessionTime && (
                            <span className="text-gray-400 font-normal"> · {nextSessionTime.slice(0, 5)}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* SUIVI */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="px-4 mt-5 relative z-10">
        {/* MONITORING — un cadre individuel par indicateur : Récupération / Charge · ACWR / Strain */}
        <SectionTitle label="Monitoring" onDetails={() => navigate("/dashboard/monitoring")} />

        {monitoringLoading ? (
          <div className="grid grid-cols-3 gap-2 mb-5">
            {[0, 1, 2].map((i) => <div key={i} className="h-[140px] rounded-2xl bg-white/5 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 mb-5">
            <button
              onClick={() => navigate("/dashboard/monitoring")}
              className="rounded-2xl px-2 py-3 relative overflow-hidden flex flex-col items-center gap-2 cursor-pointer transition-all hover:border-gray-700/80 active:scale-[0.97]"
              style={{ background: "rgba(17,24,39,0.7)", border: "1px solid rgba(31,41,55,0.6)", backdropFilter: "blur(12px)" }}
            >
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${hooperZone.color}38, transparent 70%)` }} />
              <span className="text-[10px] text-gray-500 font-semibold text-center leading-tight relative z-10">Récupération</span>
              <ClockGauge value={hooperScore} max={28} color={hooperZone.color} size={84} valueSize={18} delay={0.1} denom="/28" />
              <span className="relative z-10 inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-[9.5px] font-bold text-center leading-tight"
                style={{ color: hooperZone.color, background: `${hooperZone.color}1c`, border: `1px solid ${hooperZone.color}55` }}>
                {hooperZone.label}
              </span>
            </button>
            <button
              onClick={() => navigate("/dashboard/monitoring")}
              className="rounded-2xl px-2 py-3 relative overflow-hidden flex flex-col items-center gap-2 cursor-pointer transition-all hover:border-gray-700/80 active:scale-[0.97]"
              style={{ background: "rgba(17,24,39,0.7)", border: "1px solid rgba(31,41,55,0.6)", backdropFilter: "blur(12px)" }}
            >
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${acwr.color}38, transparent 70%)` }} />
              <span className="text-[10px] text-gray-500 font-semibold text-center leading-tight relative z-10">Charge · ACWR</span>
              <ClockGauge value={acwr.acwr} max={2} color={acwr.color} size={84} valueSize={16} delay={0.2} format={(n) => n.toFixed(2)} />
              <span className="relative z-10 inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-[9.5px] font-bold text-center leading-tight"
                style={{ color: acwr.color, background: `${acwr.color}1c`, border: `1px solid ${acwr.color}55` }}>
                {acwr.label}
              </span>
            </button>
            <button
              onClick={() => navigate("/dashboard/monitoring")}
              className="rounded-2xl px-2 py-3 relative overflow-hidden flex flex-col items-center gap-2 cursor-pointer transition-all hover:border-gray-700/80 active:scale-[0.97]"
              style={{ background: "rgba(17,24,39,0.7)", border: "1px solid rgba(31,41,55,0.6)", backdropFilter: "blur(12px)" }}
            >
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${strain.color}38, transparent 70%)` }} />
              <span className="text-[10px] text-gray-500 font-semibold text-center leading-tight relative z-10">Strain</span>
              <ClockGauge value={strain.strain} max={6000} color={strain.color} size={84} valueSize={16} delay={0.3} format={(n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${Math.round(n)}`)} />
              <span className="relative z-10 inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-[9.5px] font-bold text-center leading-tight"
                style={{ color: strain.color, background: `${strain.color}1c`, border: `1px solid ${strain.color}55` }}>
                {strain.label}
              </span>
            </button>
          </div>
        )}

        {/* NUTRITION — objectif calorique + macros du plan (pas de consommé) */}
        <SectionTitle label="Nutrition" onDetails={() => navigate("/dashboard/nutrition")} />
        <button
          onClick={() => navigate("/dashboard/nutrition")}
          className="w-full rounded-2xl p-4 relative overflow-hidden text-left cursor-pointer transition-all hover:border-gray-700/80 active:scale-[0.98]"
          style={{ background: "rgba(17,24,39,0.7)", border: "1px solid rgba(31,41,55,0.6)", backdropFilter: "blur(12px)" }}
        >
          <div
            className="absolute -top-11 -right-11 w-[180px] h-[180px] rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${plan.color}40, transparent 70%)` }}
          />
          <div className="flex items-center gap-3.5 relative z-10">
            {/* Anneau statique — affiche la cible sans mécanique de progression (pas de "consommé" à comparer) */}
            <div className="relative shrink-0 rounded-full flex flex-col items-center justify-center"
              style={{ width: 92, height: 92, border: `2px solid ${plan.color}4d`, background: `${plan.color}0f` }}>
              <span className="font-bold tabular-nums" style={{ fontSize: 19, color: "#f8fafc", lineHeight: 1, letterSpacing: -0.8, textShadow: `0 0 10px ${plan.color}80` }}>
                {caloriesPerDay.toLocaleString("fr-FR")}
              </span>
              <span className="text-[9px] font-semibold mt-1 uppercase tracking-wide" style={{ color: plan.color }}>kcal / jour</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2.5">
                <span className="text-[12.5px] font-semibold text-white">Objectif du jour</span>
                <span className="text-[9.5px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{ color: plan.color, background: `${plan.color}1c`, border: `1px solid ${plan.color}55` }}>
                  Plan {plan.shortLabel}
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                {macroRows.map((m) => (
                  <div key={m.label} className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-500 font-semibold w-[54px] shrink-0">{m.label}</span>
                    <div className="flex-1 h-[5px] rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${m.pct}%`, background: m.color, boxShadow: `0 0 6px ${m.color}88` }} />
                    </div>
                    <span className="text-[10px] text-gray-300 font-semibold tabular-nums w-8 text-right shrink-0">{m.value}g</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </button>
      </motion.div>

      {suiviSeul && (
        <AddFreeSessionSheet
          open={addSessionOpen}
          date={todayIso}
          onClose={() => setAddSessionOpen(false)}
          create={addWeekEvent}
        />
      )}

      <BottomNav />
    </div>
  )
}
